import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  IconButton,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { getLocations, bulkUploadLocations } from '../../../../api/locationApi';
import { getEntities } from '../../../../api/entityApi';
import { useErrorModal } from '../../../../hooks/useErrorModal';
import PermissionGuard from '../../../../components/PermissionGuard';
import BulkUploadModal from '../../../../components/common/BulkUploadModal';

const filterInputStyle = {
  borderRadius: '10px',
  backgroundColor: '#fff',
  transition: 'all 0.2s ease-in-out',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(226, 232, 240, 0.8)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(117, 34, 209, 0.4)',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#7522D1',
  },
};

const Location = () => {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [tab, setTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [entities, setEntities] = useState([]);
  const { showError, ErrorModal } = useErrorModal();

  const filteredLocations = useMemo(() => {
    return locations.filter((l) => l.name.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [locations, searchQuery]);

  const selectedLocation = useMemo(() => {
    return locations.find((l) => l.id === selectedLocationId);
  }, [locations, selectedLocationId]);

  const handleAddClick = () => {
    navigate('/org-management/org-structure/add-location');
  };

  const handleMenuClick = (event, location) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedLocationId(location.id);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleEdit = () => {
    if (selectedLocation) {
      navigate(`/org-management/org-structure/edit-location/${selectedLocation.id}`, {
        state: { locationData: selectedLocation }
      });
    }
    handleMenuClose();
  };

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const result = await getLocations();
      if (result.success) {
        setLocations(result.data);
        if (result.data.length > 0 && !selectedLocationId) {
          setSelectedLocationId(result.data[0].id);
        }
      } else {
        setError(result.message);
      }
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
    const fetchEntitiesList = async () => {
      try {
        const result = await getEntities();
        if (result.success) {
          setEntities(result.data.rows || result.data);
        }
      } catch (err) {
        console.error('Error fetching entities:', err);
      }
    };
    fetchEntitiesList();
  }, []);

  const handleBulkUpload = async (formData) => {
    const result = await bulkUploadLocations(formData);
    if (result.success) {
      setSnackbar({ open: true, message: result.message, severity: 'success' });
      fetchLocations();
      setIsUploadOpen(false);
    } else {
      throw new Error(result.message || 'Upload failed');
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box sx={{ width: '100%', marginTop: 0 }}>
      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          padding: '20px 24px',
          marginBottom: 3,
          borderRadius: '12px',
          border: '1px solid rgba(117, 34, 209, 0.08)',
          borderLeft: '6px solid #7522D1',
          boxShadow: '0 4px 20px rgba(117, 34, 209, 0.04)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: '#030007',
              marginBottom: 0.5,
              fontSize: '1.1rem',
            }}
          >
            Locations
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#4F4937',
              fontSize: '0.85rem',
            }}
          >
            Manage your organization's office locations
          </Typography>
        </Box>
        <PermissionGuard module="entities" action="create">
          <Box sx={{ display: 'flex' }}>
            <Button
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              onClick={() => setIsUploadOpen(true)}
              sx={{
                borderColor: 'rgba(117, 34, 209, 0.5)',
                color: '#7522D1',
                textTransform: 'none',
                fontWeight: 600,
                paddingX: 2,
                paddingY: 1,
                borderRadius: '10px',
                mr: 2,
                whiteSpace: 'nowrap',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  borderColor: '#7522D1',
                  backgroundColor: 'rgba(117, 34, 209, 0.04)',
                  transform: 'translateY(-1px)',
                },
              }}
            >
              Upload Excel
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddClick}
              sx={{
                backgroundColor: '#7522D1',
                color: '#FFFFFF',
                textTransform: 'none',
                fontWeight: 600,
                paddingX: 2.5,
                paddingY: 1.2,
                borderRadius: '10px',
                whiteSpace: 'nowrap',
                boxShadow: '0 4px 12px rgba(117, 34, 209, 0.2)',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  backgroundColor: '#5E1BA8',
                  boxShadow: '0 6px 16px rgba(117, 34, 209, 0.3)',
                  transform: 'translateY(-1px)',
                },
              }}
            >
              Add Location
            </Button>
          </Box>
        </PermissionGuard>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
        {/* Sidebar */}
        <Box sx={{ width: { xs: '100%', md: '30%' }, minWidth: { xs: '100%', md: 300 } }}>
          <TextField
            fullWidth
            placeholder="Search here"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ marginRight: 1, color: '#7522D1', fontSize: '1.2rem' }} />,
            }}
            variant="outlined"
            sx={{
              marginBottom: 2,
              '& .MuiOutlinedInput-root': filterInputStyle,
            }}
          />

          <Typography
            sx={{
              fontWeight: 600,
              color: '#030007',
              marginBottom: 2,
              fontSize: '0.9rem',
            }}
          >
            Locations ({filteredLocations.length})
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          ) : (
            filteredLocations.map((location) => (
              <Card
                key={location.id}
                onClick={() => setSelectedLocationId(location.id)}
                sx={{
                  padding: 2,
                  marginBottom: 1.5,
                  cursor: 'pointer',
                  backgroundColor: selectedLocationId === location.id ? '#F5EEFC' : '#FFFFFF',
                  border: selectedLocationId === location.id ? '1px solid #7522D1' : '1px solid rgba(117, 34, 209, 0.08)',
                  borderLeft: selectedLocationId === location.id ? '6px solid #7522D1' : '1px solid rgba(117, 34, 209, 0.08)',
                  borderRadius: '12px',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: selectedLocationId === location.id ? '0 4px 12px rgba(117, 34, 209, 0.08)' : 'none',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(117, 34, 209, 0.12)',
                    borderColor: '#7522D1',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '10px',
                      backgroundColor: 'rgba(117, 34, 209, 0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    <LocationOnIcon sx={{ color: '#7522D1', fontSize: '1.2rem' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: '#030007',
                        fontSize: '0.95rem',
                      }}
                    >
                      {location.name}
                    </Typography>
                    <Typography
                      sx={{
                        color: '#4F4937',
                        fontSize: '0.85rem',
                      }}
                    >
                      {location.city || 'No city specified'}
                    </Typography>
                  </Box>
                  <IconButton onClick={(e) => handleMenuClick(e, location)} size="small">
                    <MoreVertIcon sx={{ color: '#4F4937' }} />
                  </IconButton>
                </Box>
              </Card>
            ))
          )}
        </Box>

        {/* Main Content */}
        <Box sx={{ flex: 1 }}>
          {selectedLocation ? (
            <Card
              sx={{
                padding: 4,
                boxShadow: '0 4px 20px rgba(117, 34, 209, 0.02)',
                border: '1px solid rgba(117, 34, 209, 0.08)',
                borderRadius: '16px',
                backgroundColor: '#FFFFFF',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '12px',
                      backgroundColor: 'rgba(117, 34, 209, 0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid rgba(117, 34, 209, 0.15)',
                    }}
                  >
                    <LocationOnIcon sx={{ color: '#7522D1', fontSize: '1.6rem' }} />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 650,
                        color: '#030007',
                        fontSize: '1.1rem',
                      }}
                    >
                      {selectedLocation.name}
                    </Typography>
                    <Typography
                      sx={{
                        color: '#4F4937',
                        fontSize: '0.9rem',
                      }}
                    >
                      {selectedLocation.city || 'No city specified'}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                sx={{
                  borderBottom: '1px solid rgba(117, 34, 209, 0.08)',
                  marginBottom: 3,
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#7522D1',
                    height: '3px',
                    borderRadius: '3px 3px 0 0',
                  },
                }}
              >
                <Tab
                  label="Summary"
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    color: '#4F4937',
                    fontWeight: tab === 0 ? 600 : 400,
                    '&.Mui-selected': {
                      color: '#7522D1',
                    },
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      color: '#7522D1',
                    },
                  }}
                />
                <Tab
                  label="Details"
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    color: '#4F4937',
                    fontWeight: tab === 1 ? 600 : 400,
                    '&.Mui-selected': {
                      color: '#7522D1',
                    },
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      color: '#7522D1',
                    },
                  }}
                />
              </Tabs>

              {tab === 0 && (
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: '#030007',
                      marginBottom: 1,
                      fontSize: '0.9rem',
                    }}
                  >
                    Description
                  </Typography>
                  <Typography
                    sx={{
                      color: '#4F4937',
                      fontSize: '0.9rem',
                      marginBottom: 3,
                    }}
                  >
                    {selectedLocation.description || 'No description available for this location.'}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Typography sx={{ fontWeight: 600, color: '#030007', fontSize: '0.9rem' }}>
                        Timezone
                      </Typography>
                      <Typography sx={{ color: '#4F4937', fontSize: '0.9rem' }}>
                        {selectedLocation.timezone || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              {tab === 1 && (
                <Box>
                  <Typography sx={{ fontWeight: 600, color: '#030007', marginBottom: 2, fontSize: '0.9rem' }}>
                    Address Information
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Typography sx={{ fontWeight: 600, color: '#030007', fontSize: '0.9rem' }}>
                        Address Line 1
                      </Typography>
                      <Typography sx={{ color: '#4F4937', fontSize: '0.9rem' }}>
                        {selectedLocation.address1 || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 600, color: '#030007', fontSize: '0.9rem' }}>
                        Address Line 2
                      </Typography>
                      <Typography sx={{ color: '#4F4937', fontSize: '0.9rem' }}>
                        {selectedLocation.address2 || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 600, color: '#030007', fontSize: '0.9rem' }}>
                        City
                      </Typography>
                      <Typography sx={{ color: '#4F4937', fontSize: '0.9rem' }}>
                        {selectedLocation.city || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 600, color: '#030007', fontSize: '0.9rem' }}>
                        State
                      </Typography>
                      <Typography sx={{ color: '#4F4937', fontSize: '0.9rem' }}>
                        {selectedLocation.state || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 600, color: '#030007', fontSize: '0.9rem' }}>
                        Zip Code
                      </Typography>
                      <Typography sx={{ color: '#4F4937', fontSize: '0.9rem' }}>
                        {selectedLocation.zip || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </Card>
          ) : (
            <Card
              sx={{
                padding: 4,
                boxShadow: '0 4px 20px rgba(117, 34, 209, 0.02)',
                border: '1px solid rgba(117, 34, 209, 0.08)',
                borderRadius: '16px',
                textAlign: 'center',
                backgroundColor: '#FFFFFF',
              }}
            >
              <Typography color="text.secondary">Select a location to view details</Typography>
            </Card>
          )}
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{
            width: '100%',
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 1.5,
            minWidth: 160,
            borderRadius: '12px',
            border: '1px solid rgba(117, 34, 209, 0.08)',
            boxShadow: '0 10px 30px rgba(117, 34, 209, 0.08)',
            overflow: 'visible',
            '& .MuiMenuItem-root': {
              fontSize: '0.9rem',
              padding: '10px 16px',
              color: '#42374F',
              borderRadius: '8px',
              margin: '4px 8px',
              transition: 'all 0.15s ease-in-out',
              '&:hover': {
                backgroundColor: 'rgba(117, 34, 209, 0.05)',
                color: '#7522D1',
              },
            },
          },
        }}
      >
        <PermissionGuard module="entities" action="edit">
          <MenuItem onClick={handleEdit}>
            <EditIcon sx={{ mr: 1.5, fontSize: 18 }} />
            Edit
          </MenuItem>
        </PermissionGuard>
      </Menu>

      <ErrorModal />
      <BulkUploadModal
        open={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        title="Upload Locations"
        onUpload={handleBulkUpload}
        entityRequired={true}
        entities={entities}
      />
    </Box>
  );
};

export default Location;
